/**
 * Flutter WebView deep link integration.
 *
 * When the app runs inside the Flutter WebView:
 * 1. Initial load: use window.location.pathname and window.location.search (or your router).
 * 2. New link while app is open: listen for 'flutterDeepLink' and use event.detail.
 * 3. Ask Flutter for last deep link: call getDeepLinkParams().
 *
 * Deep link URL format: HTTPS Universal Links / App Links (e.g. https://d1q1chclchjxl4.cloudfront.net/practice)
 * Web receives same path + query, e.g. /practice
 */

import { useEffect, useState } from 'react';

export interface FlutterDeepLinkParams {
  path?: string;
  pathSegments?: string[];
  query?: string;
  queryParameters?: Record<string, string>;
}

/**
 * Returns whether the app is running inside the Flutter WebView.
 */
export function useIsFlutterWebView(): boolean {
  const [isFlutter, setIsFlutter] = useState(false);
  useEffect(() => {
    setIsFlutter(
      typeof window !== 'undefined' &&
        typeof (window as Window & { flutter_inappwebview?: unknown }).flutter_inappwebview !==
          'undefined',
    );
  }, []);
  return isFlutter;
}

/**
 * Returns the last deep link params from Flutter (path + query).
 * Call once on mount if you need to know how the app was opened.
 * Returns null when not in Flutter WebView or on error.
 */
export async function getDeepLinkParams(): Promise<FlutterDeepLinkParams | null> {
  const win =
    typeof window !== 'undefined'
      ? (window as Window & {
          flutter_inappwebview?: { callHandler: (name: string) => Promise<FlutterDeepLinkParams> };
        })
      : null;
  if (!win?.flutter_inappwebview) return null;
  try {
    const params = await win.flutter_inappwebview.callHandler('getDeepLinkParams');
    return (params as FlutterDeepLinkParams) ?? null;
  } catch {
    return null;
  }
}

/**
 * Subscribe to deep link events (when user opens a new link while app is open).
 * Your router can use path + queryParameters to navigate.
 */
export function useFlutterDeepLink(onDeepLink: (params: FlutterDeepLinkParams) => void): void {
  useEffect(() => {
    const handler = (event: CustomEvent<FlutterDeepLinkParams>) => {
      const detail = event.detail ?? {};
      onDeepLink({
        path: detail.path,
        pathSegments: detail.pathSegments ?? [],
        query: detail.query,
        queryParameters: detail.queryParameters ?? {},
      });
    };
    window.addEventListener('flutterDeepLink', handler as EventListener);
    return () => window.removeEventListener('flutterDeepLink', handler as EventListener);
  }, [onDeepLink]);
}
